// Route prefetch map - maps route paths to their dynamic imports
// This allows prefetching page chunks on link hover/touch before navigation

const routeImportMap: Record<string, () => Promise<unknown>> = {
  // Admin routes
  "/admin": () => import("@/pages/admin/AdminDashboard"),
  "/admin/modelos": () => import("@/pages/admin/AdminModelos"),
  "/admin/funcionarios": () => import("@/pages/admin/AdminFuncionarios"),
  "/admin/usuarios": () => import("@/pages/admin/AdminUsuarios"),
  "/admin/atividade-recente": () => import("@/pages/admin/AdminAtividadeRecente"),
  "/admin/financeiro": () => import("@/pages/admin/AdminFinanceiro"),
  "/admin/pagamentos": () => import("@/pages/admin/AdminPagamentos"),
  "/admin/chatter-billing": () => import("@/pages/admin/AdminChatterBilling"),
  "/admin/snapsell": () => import("@/pages/admin/AdminSnapsell"),
  "/admin/conteudo-exclusivo-aprovacao": () => import("@/pages/admin/AdminConteudoExclusivo"),
  "/admin/conteudo-oculto": () => import("@/pages/admin/AdminConteudoOculto"),
  "/admin/solicitacoes": () => import("@/pages/admin/AdminSolicitacoes"),
  "/admin/chat": () => import("@/pages/UniversalChat"),
  "/admin/configuracoes": () => import("@/pages/admin/AdminConfiguracoes"),

  // Employee routes
  "/employee": () => import("@/pages/employee/EmployeeDashboard"),
  "/employee/modelos": () => import("@/pages/employee/EmployeeModelos"),
  "/employee/calendario": () => import("@/pages/employee/EmployeeCalendario"),
  "/employee/conteudo-exclusivo": () => import("@/pages/employee/EmployeeConteudoExclusivo"),
  "/employee/conteudo-exclusivo-aprovacao": () => import("@/pages/employee/EmployeeConteudoExclusivoAprovacao"),
  "/employee/conteudo-oculto": () => import("@/pages/employee/EmployeeConteudoOculto"),
  "/employee/mensagens": () => import("@/pages/UniversalChat"),
  "/employee/chat": () => import("@/pages/employee/EmployeeChat"),
  "/employee/financeiro": () => import("@/pages/employee/EmployeeFinanceiro"),
  "/employee/financeiro-chat": () => import("@/pages/employee/EmployeeFinanceiroChat"),
  "/employee/configuracoes": () => import("@/pages/employee/EmployeeConfiguracoes"),
  "/employee/post-manager": () => import("@/pages/employee/EmployeePostManager"),
  "/employee/funcionarios": () => import("@/pages/employee/EmployeeFuncionarios"),
  "/employee/usuarios": () => import("@/pages/employee/EmployeeUsuarios"),
  "/employee/chatters": () => import("@/pages/employee/EmployeeChatters"),
  "/employee/chatters/multas": () => import("@/pages/employee/EmployeeChatterFines"),
  "/employee/chatters/registros": () => import("@/pages/employee/ChatterDailyEntries"),
  "/employee/supervisor/semanal": () => import("@/pages/employee/SupervisorWeeklyEntries"),
  "/employee/supervisor/meeting": () => import("@/pages/employee/SupervisorMeeting"),
  "/employee/supervisor/horarios": () => import("@/pages/employee/SupervisorScheduleView"),
  "/employee/horarios": () => import("@/pages/employee/ChatterWeeklySchedule"),
  "/employee/meeting": () => import("@/pages/employee/ChatterMeeting"),
  "/employee/pagamentos": () => import("@/pages/employee/EmployeePagamentos"),
  "/employee/pagamentos-socios": () => import("@/pages/employee/EmployeePagamentosSocios"),
  "/employee/meu-faturamento": () => import("@/pages/employee/ChatterBillingHistory"),
  "/employee/chatter-billing": () => import("@/pages/employee/EmployeeChatterBilling"),
  "/employee/snapsell": () => import("@/pages/employee/EmployeeSnapsell"),
  "/employee/atividade-recente": () => import("@/pages/employee/EmployeeAtividadeRecente"),

  // Recruiter routes
  "/recruiter": () => import("@/pages/recruiter/RecruiterDashboard"),
  "/recruiter/modelos": () => import("@/pages/recruiter/RecruiterModelos"),
  "/recruiter/loja": () => import("@/pages/recruiter/RecruiterLoja"),
  "/recruiter/perfil": () => import("@/pages/recruiter/RecruiterProfile"),

  // Recruiter Admin routes
  "/recruiter-admin": () => import("@/pages/recruiter-admin/RecruiterAdminCommissions"),
  "/recruiter-admin/calendario": () => import("@/pages/recruiter-admin/RecruiterAdminCalendar"),
  "/recruiter-admin/modelos-afiliados": () => import("@/pages/recruiter-admin/RecruiterAdminAffiliateModels"),
  "/recruiter-admin/recrutadores": () => import("@/pages/recruiter-admin/RecruiterAdminRecruiters"),
  "/recruiter-admin/loja": () => import("@/pages/recruiter-admin/RecruiterAdminLoja"),
  "/recruiter-admin/perfil": () => import("@/pages/recruiter-admin/RecruiterAdminProfile"),

  // Model routes
  "/financeiro": () => import("@/pages/Financeiro"),
  "/banco": () => import("@/pages/Banco"),
  "/contato": () => import("@/pages/Contato"),
  "/sobre": () => import("@/pages/Sobre"),
  "/profile": () => import("@/pages/Profile"),
  "/mensagens": () => import("@/pages/ModelChat"),
  "/contrato": () => import("@/pages/Contrato"),
  "/calendario": () => import("@/pages/Calendario"),
  "/conteudo-exclusivo": () => import("@/pages/ConteudoExclusivo"),
  "/afiliado": () => import("@/pages/Afiliado"),
};

const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a route's JS chunk. Safe to call multiple times — only fetches once.
 */
export function prefetchRoute(path: string) {
  // Strip query params
  const cleanPath = path.split("?")[0];
  
  if (prefetchedRoutes.has(cleanPath)) return;
  
  const importer = routeImportMap[cleanPath];
  if (importer) {
    prefetchedRoutes.add(cleanPath);
    importer().catch(() => {
      // Remove from set so it can retry
      prefetchedRoutes.delete(cleanPath);
    });
  }
}

/**
 * Props to spread on a Link element to enable prefetch on hover/touch.
 */
export function getPrefetchProps(href: string) {
  return {
    onMouseEnter: () => prefetchRoute(href),
    onTouchStart: () => prefetchRoute(href),
  };
}
